import { useEffect, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import * as adminService from '@/services/adminService';

const periods = [
  { value: 7, label: '7 dias' },
  { value: 30, label: '30 dias' },
  { value: 90, label: '90 dias' },
];

const formatNumber = (value) => Number(value || 0).toLocaleString('pt-PT');

const ChartCard = ({ title, children }) => (
  <section className="rounded-lg border border-fg/10 bg-fg/[0.04] p-4">
    <h2 className="font-serif text-xl font-black text-accent">{title}</h2>
    <div className="mt-4 h-80">{children}</div>
  </section>
);

export default function AdminAnalyticsPage() {
  const [period, setPeriod] = useState(30);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = async (days = period) => {
    setLoading(true);
    try {
      const res = await adminService.getAnalytics(days);
      setAnalytics(res.data.data || {});
      setError('');
    } catch {
      setError('Não foi possível carregar os dados de analytics.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(period);
  }, [period]);

  const growth = useMemo(() => (analytics?.growth || []).map((item) => ({
    ...item,
    label: new Date(`${item.date}T00:00:00`).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' }),
    count: Number(item.count || 0),
  })), [analytics]);

  const topPOIs = useMemo(() => (analytics?.topPOIs || []).map((poi) => ({
    name: poi.name?.length > 20 ? `${poi.name.slice(0, 20)}...` : poi.name,
    visitas: Number(poi.totalVisits || 0),
    categoria: poi.category || '-',
  })), [analytics]);

  const totals = useMemo(() => ({
    newPlayers: growth.reduce((sum, item) => sum + Number(item.count || 0), 0),
    bestDay: growth.reduce((best, item) => Number(item.count || 0) > Number(best.count || 0) ? item : best, {}),
    poiVisits: topPOIs.reduce((sum, item) => sum + Number(item.visitas || 0), 0),
    activePois: topPOIs.length,
  }), [growth, topPOIs]);

  return (
    <section className="min-h-full">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-accent/70">Inteligência operacional</p>
          <h1 className="mt-1 font-serif text-3xl font-black text-accent">Analytics</h1>
          <p className="mt-2 max-w-2xl text-sm text-fg/55">
            Acompanha crescimento de jogadores e os pontos de interesse com maior atividade.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {periods.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => setPeriod(item.value)}
              className={`rounded-lg border px-4 py-2 text-sm font-bold ${period === item.value ? 'border-accent/40 bg-accent/15 text-accent' : 'border-fg/10 bg-black/20 text-fg/50 hover:text-fg'}`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mt-5 rounded-lg border border-energy/40 bg-energy/10 p-4 text-sm font-bold text-energy">{error}</div>
      )}

      <div className="mt-6 grid gap-3 md:grid-cols-4">
        {[
          ['Novos jogadores', totals.newPlayers],
          ['Melhor dia', totals.bestDay.count || 0],
          ['Visitas top POIs', totals.poiVisits],
          ['POIs analisados', totals.activePois],
        ].map(([label, value]) => (
          <div key={label} className="rounded-lg border border-fg/10 bg-fg/[0.04] p-4">
            <div className="text-xs font-bold uppercase tracking-[0.16em] text-fg/35">{label}</div>
            <div className="mt-2 font-serif text-3xl font-black text-accent">{formatNumber(value)}</div>
            {label === 'Melhor dia' && <div className="mt-1 text-xs text-fg/35">{totals.bestDay.label || '-'}</div>}
          </div>
        ))}
      </div>

      <div className="mt-6 grid gap-4 xl:grid-cols-5">
        <div className="xl:col-span-3">
          <ChartCard title={`Crescimento nos últimos ${period} dias`}>
            {growth.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={growth} margin={{ left: -20, right: 10, top: 10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="adminAnalyticsGrowth" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.75} />
                      <stop offset="95%" stopColor="#D4AF37" stopOpacity={0.04} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                  <XAxis dataKey="label" stroke="rgba(255,255,255,0.45)" tick={{ fontSize: 11 }} />
                  <YAxis stroke="rgba(255,255,255,0.45)" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: '#111827', border: '1px solid rgba(212,175,55,0.3)', borderRadius: 8 }} />
                  <Area type="monotone" dataKey="count" name="Novos jogadores" stroke="#D4AF37" strokeWidth={3} fill="url(#adminAnalyticsGrowth)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="grid h-full place-items-center text-sm font-bold text-fg/40">{loading ? 'A carregar...' : 'Sem dados de crescimento.'}</div>
            )}
          </ChartCard>
        </div>

        <div className="xl:col-span-2">
          <ChartCard title="POIs com mais visitas">
            {topPOIs.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topPOIs} layout="vertical" margin={{ left: 20, right: 10, top: 8, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.08)" horizontal={false} />
                  <XAxis type="number" stroke="rgba(255,255,255,0.45)" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" stroke="rgba(255,255,255,0.45)" width={120} tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: '#111827', border: '1px solid rgba(58,143,209,0.3)', borderRadius: 8 }} />
                  <Bar dataKey="visitas" name="Visitas" fill="#3A8FD1" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="grid h-full place-items-center text-sm font-bold text-fg/40">{loading ? 'A carregar...' : 'Sem visitas registadas.'}</div>
            )}
          </ChartCard>
        </div>
      </div>

      <section className="mt-6 rounded-lg border border-fg/10 bg-fg/[0.04] p-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-serif text-xl font-black text-accent">Leitura rápida</h2>
          <span className="rounded-full border border-fg/10 bg-black/20 px-3 py-1 text-xs font-bold text-fg/55">
            {loading ? 'A carregar' : 'Atualizado'}
          </span>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <div className="rounded-lg border border-fg/10 bg-black/15 p-3">
            <div className="text-xs font-bold uppercase tracking-[0.16em] text-fg/35">Retenção inicial</div>
            <p className="mt-2 text-sm text-fg/60">Usa o crescimento diário para cruzar campanhas, cidades ativas e dias com maior entrada de jogadores.</p>
          </div>
          <div className="rounded-lg border border-fg/10 bg-black/15 p-3">
            <div className="text-xs font-bold uppercase tracking-[0.16em] text-fg/35">Conteúdo urbano</div>
            <p className="mt-2 text-sm text-fg/60">Os POIs mais visitados ajudam a decidir onde criar missões, ofertas e novas zonas.</p>
          </div>
          <div className="rounded-lg border border-fg/10 bg-black/15 p-3">
            <div className="text-xs font-bold uppercase tracking-[0.16em] text-fg/35">Próxima ação</div>
            <p className="mt-2 text-sm text-fg/60">Compara este painel com fraude e resgates antes de mexer nas recompensas.</p>
          </div>
        </div>
      </section>
    </section>
  );
}
